// hooks/useLoans.ts

import { UseQueryResult } from '@tanstack/react-query';
import React from 'react';
import { useGetLoanBalance } from '@/shared/api/client/loan-balance';
import {
    ActionFlowAccessGuideDTO,
    AppapproDTO,
    LoanBalanceDto,
    LoanBalanceRequestDto,
    TransactionDTO
} from '@/shared/api/model';
import { 
    useGetLoanRepayment, 
    useGetLoanApplications, 
    useGetGuaranteedLoans,
    useGetLoanApprovalGuide 
} from '@/shared/api/client/loans';
import { useGenerateLoanSchedule } from '@/shared/api/client/loan-schedule';

// Query for getting account balances
export const useLoanRepayments = (branch: string, custNo: string): [UseQueryResult<TransactionDTO[], Error>, () => void] => {
    const query = useGetLoanRepayment(branch, custNo);
    
    const refresh = () => {
        query.refetch();
    };
    
    return [
        // @ts-ignore
        {
            ...query,
            data: query.data,
            isLoading: query.isPending,
        } as UseQueryResult<TransactionDTO[], Error>,
        refresh
    ];
};

// Query for getting loan applications
export const useLoanApplications = (branch: string, custNo: string): [UseQueryResult<AppapproDTO[], Error>, () => void] => {
    const query = useGetLoanApplications(branch, custNo);
    
    const refresh = () => {
        query.refetch();
    };

    return [
        // @ts-ignore
        {
            ...query,
            data: query.data,
            isLoading: query.isPending,
        } as UseQueryResult<AppapproDTO[], Error>,
        refresh
    ];
};

// Query for getting guaranteed loans
export const useGuaranteedLoans = (branch: string, custNo: string): [UseQueryResult<AppapproDTO[], Error>, () => void] => {
    const query = useGetGuaranteedLoans(branch, custNo);
    
    const refresh = () => {
        query.refetch();
    };
    
    return [
        // @ts-ignore
        {
            ...query,
            data: query.data,
            isLoading: query.isPending,
        } as UseQueryResult<AppapproDTO[], Error>,
        refresh
    ];
};

// Hook for fetching loan schedule
export const useLoanSchedule = (appapproDTO: AppapproDTO |null) => {
    const generateScheduleMutation = useGenerateLoanSchedule();
    const [isLoading, setIsLoading] = React.useState(false);
    
    // Use a ref to track the timeout for debouncing
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    
    // Use a ref to track the last DTO to avoid unnecessary calls
    const lastDTORef = React.useRef<string | null>(null);
    
    const query = {
        isPending: isLoading || generateScheduleMutation.isPending,
        isError: generateScheduleMutation.isError,
        error: generateScheduleMutation.error,
        data: generateScheduleMutation.data,
        refetch: () => {
            if (appapproDTO) {
                // @ts-ignore
                generateScheduleMutation.mutate({ data: appapproDTO });
            }
        }
    };
    
    // Generate schedule when appapproDTO changes with debouncing
    React.useEffect(() => {
        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        if (appapproDTO) {
            // Create a stable string representation for comparison
            const dtoString = JSON.stringify({
                branch: appapproDTO.branch,
                custNo: appapproDTO.custNo,
                glNo: appapproDTO.glNo,
                loanAmount: appapproDTO.loanAmount,
                loanPeriod: appapproDTO.loanPeriod,
                unpaidPrincipal: appapproDTO.unpaidPrincipal,
                unpaidAccruedInterest: appapproDTO.unpaidAccruedInterest,
                refinanceMode: appapproDTO.refinanceMode,
                topupMode: appapproDTO.topupMode
            });
            
            // Only proceed if the DTO has actually changed
            if (dtoString === lastDTORef.current) {
                console.log('useLoanSchedule: Skipping duplicate call - DTO unchanged');
                return;
            }
            
            console.log('useLoanSchedule: Scheduling API call in 300ms for:', {
                glNo: appapproDTO.glNo,
                amount: appapproDTO.loanAmount,
                period: appapproDTO.loanPeriod
            });
            
            setIsLoading(true);
            
            // Minimal debounce (300ms) since the DTO is already debounced in useLoanApplication
            // This just prevents rapid successive calls if the DTO updates multiple times
            timeoutRef.current = setTimeout(() => {
                console.log('useLoanSchedule: Executing API call for:', {
                    glNo: appapproDTO.glNo,
                    amount: appapproDTO.loanAmount,
                    period: appapproDTO.loanPeriod
                });
                lastDTORef.current = dtoString;
                // @ts-ignore
                generateScheduleMutation.mutate({ data: appapproDTO });
                setIsLoading(false);
            }, 300);
        }
        
        // Cleanup function
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [appapproDTO]);
    
    return [query, query.refetch] as [UseQueryResult<any, Error>, () => void];
};

// Query for getting loan applications
export const useLoanApprovalGuide = (glNo?: string) => {
    // @ts-ignore
    const query = useGetLoanApprovalGuide({ glNo }, {
        query: {
            enabled: (glNo != null)
        }
    });
    
    const refresh = () => {
        query.refetch();
    };
    return [
        // @ts-ignore
        {
            ...query,
            data: query.data,
            isLoading: query.isPending,
        } as UseQueryResult<ActionFlowAccessGuideDTO[], Error>,
        refresh
    ];
};

// Hook for checking loan balance
export const useLoanBalanceCheck = () => {
    const loanBalanceMutation = useGetLoanBalance();
    
    const checkLoanBalance = async (
        branch: string,
        glNo: string,
        custNo: string,
        cycle: number = 1
    ): Promise<LoanBalanceDto | null> => {
        try {
            const request: LoanBalanceRequestDto = {
                branch,
                glNo,
                custNo,
                cycle,
                trxDate: new Date().toISOString().split('T')[0]
            };
            
            const response = await loanBalanceMutation.mutateAsync({ data: request });
            return (response as any)?.data ?? response;
        } catch (error) {
            console.error("Error checking loan balance:", error);
            return null;
        }
    };
    
    return {
        checkLoanBalance,
        isChecking: loanBalanceMutation.isPending
    };
};
